/**
 * Dashboard Page — professional financial overview.
 *
 * Rebuilt to surface EVERY voucher type the company actually has (Receipts,
 * Payments, Sales, Purchases) instead of only Sales. All data is read through
 * the pre-existing backend endpoints — no sync/backend logic was changed.
 */

import React, { useEffect, useMemo, useState } from 'react';
import SummaryCard from '../components/SummaryCard';
import CashFlowCharts from '../components/CashFlowCharts';
import OutstandingItems from '../components/OutstandingItems';
import ErrorAlert from '../components/ErrorAlert';
import TransactionsSection from '../components/TransactionsSection';
import JournalEntriesSection from '../components/JournalEntriesSection';
import LedgerSection from '../components/LedgerSection';
import TrialBalanceSection from '../components/TrialBalanceSection';
import {
  useCompanies,
  useReceivables,
  usePayables,
  useAllVouchers,
  useSyncTrigger,
} from '../hooks/useDashboard';
import { dateFilterOptions, computeRangeForFilter } from '../utils/dateFilters';
import { logout } from '../services/authAPI';
import './DashboardPage.css';

const fmtMoney = (v) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Math.abs(Number(v) || 0));

const fmtSigned = (v) => {
  const n = Number(v) || 0;
  return `${n < 0 ? '−' : ''}${fmtMoney(n)}`;
};

const fmtCount = (v) => new Intl.NumberFormat('en-IN').format(Number(v) || 0);

const DashboardPage = ({ user }) => {
  const [companyId, setCompanyId] = useState('all');
  const [dateFilter, setDateFilter] = useState('overall');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');

  const { data: companies } = useCompanies();
  const { status: syncStatus, job: syncJob, error: syncError, startSync } = useSyncTrigger();
  const [reloadPending, setReloadPending] = useState(false);

  useEffect(() => {
    if (syncStatus === 'completed' && !reloadPending) {
      setReloadPending(true);
      setTimeout(() => window.location.reload(), 1200);
    }
  }, [syncStatus, reloadPending]);

  const { from, to } = useMemo(() => {
    if (dateFilter === 'custom') return { from: customFromDate, to: customToDate };
    return computeRangeForFilter(dateFilter);
  }, [dateFilter, customFromDate, customToDate]);

  const { data: vouchers, loading: vLoading, error: vError } = useAllVouchers(companyId, companies, from, to);
  const { data: receivables } = useReceivables(companyId);
  const { data: payables } = usePayables(companyId);

  const isAllCompanies = companyId === 'all';

  // ── KPI aggregates (all computed from the merged voucher set) ──────────────
  const kpis = useMemo(() => {
    const acc = {
      receipts: 0, receiptsCount: 0,
      payments: 0, paymentsCount: 0,
      sales: 0, salesCount: 0,
      purchase: 0, purchaseCount: 0,
    };
    const parties = new Set();
    (vouchers || []).forEach((v) => {
      const amt = Math.abs(Number(v.amount) || 0);
      if (v.party && v.party !== '—') parties.add(v.party);
      switch (v.type) {
        case 'Receipt': acc.receipts += amt; acc.receiptsCount += 1; break;
        case 'Payment': acc.payments += amt; acc.paymentsCount += 1; break;
        case 'Sales': acc.sales += amt; acc.salesCount += 1; break;
        case 'Purchase': acc.purchase += amt; acc.purchaseCount += 1; break;
        default: break;
      }
    });
    return {
      ...acc,
      netCashFlow: acc.receipts - acc.payments,
      totalVouchers: (vouchers || []).length,
      activeParties: parties.size,
    };
  }, [vouchers]);

  const rcvTotal = receivables?.total || 0;
  const payTotal = payables?.total || 0;
  const netPosition = rcvTotal - payTotal;

  const handleReset = () => {
    setCompanyId('all');
    setDateFilter('overall');
    setCustomFromDate('');
    setCustomToDate('');
  };

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <div className="header-brand">
          <div className="brand-mark">₹</div>
          <div>
            <h1>Tally Financial Dashboard</h1>
            <p>Live overview of receipts, payments and outstanding across your companies.</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="sync-button"
            onClick={startSync}
            disabled={syncStatus === 'running'}
          >
            {syncStatus === 'running' ? 'Syncing…' : '↻ Sync Now'}
          </button>
          {syncStatus === 'running' && (
            <span className="sync-status-text">Pulling latest data from Tally…</span>
          )}
          {syncStatus === 'completed' && (
            <span className="sync-status-text sync-status-success">Sync complete — refreshing…</span>
          )}
          {syncStatus === 'failed' && (
            <span className="sync-status-text sync-status-error">
              Sync failed{syncError ? `: ${syncError}` : ''}{syncJob?.error ? `: ${syncJob.error}` : ''}
            </span>
          )}

          <div className="header-account">
            {user?.username && <span className="header-user">{user.username}</span>}
            <button type="button" className="logout-button" onClick={logout}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <section className="dashboard-filters">
          <div className="filter-group">
            <label htmlFor="company-select">Company</label>
            <select id="company-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="all">All Companies</option>
              {companies?.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="date-filter">Period</label>
            <select id="date-filter" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
              {dateFilterOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {dateFilter === 'custom' && (
            <>
              <div className="filter-group">
                <label htmlFor="from-date">From</label>
                <input id="from-date" type="date" value={customFromDate} onChange={(e) => setCustomFromDate(e.target.value)} />
              </div>
              <div className="filter-group">
                <label htmlFor="to-date">To</label>
                <input id="to-date" type="date" value={customToDate} onChange={(e) => setCustomToDate(e.target.value)} />
              </div>
            </>
          )}

          <div className="filter-group filter-actions">
            <button type="button" className="reset-button" onClick={handleReset}>Reset</button>
          </div>
        </section>

        {vError && <ErrorAlert message={`Data Error: ${vError}`} onDismiss={handleReset} />}

        {/* ── KPI GRID (10 metrics) ─────────────────────────────────────── */}
        <section className="kpi-grid">
          <SummaryCard title="Total Receipts" value={fmtMoney(kpis.receipts)} subtitle={`${fmtCount(kpis.receiptsCount)} vouchers`} icon="⬇️" color="success" />
          <SummaryCard title="Total Payments" value={fmtMoney(kpis.payments)} subtitle={`${fmtCount(kpis.paymentsCount)} vouchers`} icon="⬆️" color="danger" />
          <SummaryCard title="Net Cash Flow" value={fmtSigned(kpis.netCashFlow)} subtitle="Receipts − Payments" icon="💵" color={kpis.netCashFlow >= 0 ? 'success' : 'danger'} />
          <SummaryCard title="Total Sales" value={fmtMoney(kpis.sales)} subtitle={`${fmtCount(kpis.salesCount)} invoices`} icon="📈" color="info" />
          <SummaryCard title="Total Purchases" value={fmtMoney(kpis.purchase)} subtitle={`${fmtCount(kpis.purchaseCount)} bills`} icon="📦" color="info" />
          <SummaryCard title="Receivables" value={fmtMoney(rcvTotal)} subtitle={`${fmtCount(receivables?.count || 0)} parties`} icon="👥" color="warning" />
          <SummaryCard title="Payables" value={fmtMoney(payTotal)} subtitle={`${fmtCount(payables?.count || 0)} parties`} icon="💳" color="danger" />
          <SummaryCard title="Net Position" value={fmtSigned(netPosition)} subtitle="Receivables − Payables" icon="⚖️" color={netPosition >= 0 ? 'success' : 'danger'} />
          <SummaryCard title="Total Vouchers" value={fmtCount(kpis.totalVouchers)} subtitle={`${isAllCompanies ? (companies?.length || 0) + ' companies' : 'selected company'}`} icon="🧾" color="default" />
          <SummaryCard title="Active Parties" value={fmtCount(kpis.activeParties)} subtitle="With transactions" icon="🏷️" color="default" />
        </section>

        {/* ── CHARTS ────────────────────────────────────────────────────── */}
        <CashFlowCharts vouchers={vouchers} />

        {/* ── OUTSTANDING PANELS ────────────────────────────────────────── */}
        {/* <section className="outstanding-grid">
          <div className="panel">
            <OutstandingItems
              title="Top Receivables"
              items={(receivables?.data || []).slice(0, 8)}
              total={rcvTotal}
            />
          </div>
          <div className="panel">
            <OutstandingItems
              title="Top Payables"
              items={(payables?.data || []).slice(0, 8)}
              total={payTotal}
            />
          </div>
        </section> */}

        {/* ── TRANSACTIONS (own Company + Period filter) ──────────────────── */}
        <TransactionsSection companies={companies} />

        {/* ── JOURNAL ENTRIES (own Company + Period filter) ───────────────── */}
        <JournalEntriesSection companies={companies} />

        {/* ── LEDGER (own Company filter) ──────────────────────────────────── */}
        <LedgerSection companies={companies} />

        {/* ── TRIAL BALANCE (own Company filter) ───────────────────────────── */}
        <TrialBalanceSection companies={companies} />
      </main>
    </div>
  );
};

export default DashboardPage;
